import { EditorStarterItem } from '../../items/item-type';
import { TrackType } from '../../state/types';

/**
 * Transition 적용 가능한 아이템인지 확인
 * 같은 트랙에 인접한 모든 아이템에 트랜지션 적용 가능
 */
export const isTransitionableItem = (_item: EditorStarterItem): boolean => {
  // 모든 아이템 타입에 트랜지션 적용 가능
  return true;
};

// 두 클립 사이의 경계 정보
export interface TransitionEdge {
  // 앞쪽 클립 (outgoing)
  sourceItemId: string;
  // 뒤쪽 클립 (incoming)
  targetItemId: string;
  // 경계의 프레임 위치 (sourceItem의 끝 = targetItem의 시작)
  edgeFrame: number;
  // 해당 트랙 ID
  trackId: string;
  // 해당 트랙 인덱스
  trackIndex: number;
}

interface FindNearestEdgeParams {
  // 마우스 X 위치에서 계산된 프레임
  frame: number;
  // 마우스 Y 위치에서 계산된 트랙 인덱스
  trackIndex: number;
  // 트랙 목록
  tracks: TrackType[];
  // 아이템 목록
  items: Record<string, EditorStarterItem>;
  // 경계 감지 범위 (프레임)
  threshold?: number;
}

// 미리 정렬된 아이템을 사용하는 최적화된 버전을 위한 파라미터
interface FindNearestEdgeWithSortedItemsParams {
  // 마우스 X 위치에서 계산된 프레임
  frame: number;
  // 마우스 Y 위치에서 계산된 트랙 인덱스
  trackIndex: number;
  // 트랙 목록
  tracks: TrackType[];
  // 트랙별로 미리 정렬된 아이템 맵 (Map<trackIndex, sortedItems>)
  sortedItemsByTrack: Map<number, EditorStarterItem[]>;
  // 경계 감지 범위 (프레임)
  threshold?: number;
}

// 같은 트랙 내에서 인접한 두 클립의 경계를 찾습니다
export const findNearestTransitionEdge = ({
  frame,
  trackIndex,
  tracks,
  items,
  threshold = 10, // 기본 10프레임 범위
}: FindNearestEdgeParams): TransitionEdge | null => {
  const track = tracks[trackIndex];
  if (!track) return null;

  // 트랙의 아이템들을 시작 프레임 순으로 정렬
  const trackItems = track.items
    .map((itemId) => items[itemId])
    .filter((item): item is EditorStarterItem => !!item)
    .sort((a, b) => a.from - b.from);

  if (trackItems.length < 2) return null;

  // 인접한 두 클립의 경계 찾기
  let nearestEdge: TransitionEdge | null = null;
  let minDistance = Infinity;

  for (let i = 0; i < trackItems.length - 1; i++) {
    const sourceItem = trackItems[i];
    const targetItem = trackItems[i + 1];

    if (!sourceItem || !targetItem) continue;

    // Transition 적용 가능한 아이템 타입인지 확인 (image, video, gif만 허용)
    if (
      !isTransitionableItem(sourceItem) ||
      !isTransitionableItem(targetItem)
    ) {
      continue;
    }

    // 두 클립이 정확히 인접해 있는지 확인 (빈 프레임이 없어야 함)
    const sourceEnd = sourceItem.from + sourceItem.durationInFrames;
    const targetStart = targetItem.from;

    // 정확히 인접해 있는 경우만 허용 (gap === 0)
    if (sourceEnd !== targetStart) continue;

    // 경계 위치
    const edgeFrame = sourceEnd;
    const distance = Math.abs(frame - edgeFrame);

    if (distance <= threshold && distance < minDistance) {
      minDistance = distance;
      nearestEdge = {
        sourceItemId: sourceItem.id,
        targetItemId: targetItem.id,
        edgeFrame,
        trackId: track.id,
        trackIndex,
      };
    }
  }

  return nearestEdge;
};

/**
 * 미리 정렬된 아이템을 사용하여 가장 가까운 트랜지션 경계를 찾습니다.
 * onDragOver에서 반복 호출 시 성능 최적화를 위해 사용됩니다.
 * 정렬 비용 O(N log N)을 제거하여 O(N)으로 감소시킵니다.
 */
export const findNearestTransitionEdgeWithSortedItems = ({
  frame,
  trackIndex,
  tracks,
  sortedItemsByTrack,
  threshold = 10,
}: FindNearestEdgeWithSortedItemsParams): TransitionEdge | null => {
  const track = tracks[trackIndex];
  if (!track) return null;

  // 캐시된 정렬된 아이템 사용
  const trackItems = sortedItemsByTrack.get(trackIndex);
  if (!trackItems || trackItems.length < 2) return null;

  // 인접한 두 클립의 경계 찾기
  let nearestEdge: TransitionEdge | null = null;
  let minDistance = Infinity;

  for (let i = 0; i < trackItems.length - 1; i++) {
    const sourceItem = trackItems[i];
    const targetItem = trackItems[i + 1];

    if (!sourceItem || !targetItem) continue;

    // Transition 적용 가능한 아이템 타입인지 확인
    if (
      !isTransitionableItem(sourceItem) ||
      !isTransitionableItem(targetItem)
    ) {
      continue;
    }

    // 두 클립이 정확히 인접해 있는지 확인
    const sourceEnd = sourceItem.from + sourceItem.durationInFrames;
    const targetStart = targetItem.from;

    if (sourceEnd !== targetStart) continue;

    // 경계 위치
    const edgeFrame = sourceEnd;
    const distance = Math.abs(frame - edgeFrame);

    if (distance <= threshold && distance < minDistance) {
      minDistance = distance;
      nearestEdge = {
        sourceItemId: sourceItem.id,
        targetItemId: targetItem.id,
        edgeFrame,
        trackId: track.id,
        trackIndex,
      };
    }
  }

  return nearestEdge;
};

/**
 * 모든 트랙의 아이템을 시작 프레임 순으로 정렬하여 캐시용 Map 생성
 * 드래그 시작 시 한 번만 호출하여 캐싱합니다.
 */
export const buildSortedItemsByTrack = (
  tracks: TrackType[],
  items: Record<string, EditorStarterItem>,
): Map<number, EditorStarterItem[]> => {
  const sortedMap = new Map<number, EditorStarterItem[]>();

  tracks.forEach((track, trackIndex) => {
    const trackItems = track.items
      .map((itemId) => items[itemId])
      .filter((item): item is EditorStarterItem => !!item)
      .sort((a, b) => a.from - b.from);
    sortedMap.set(trackIndex, trackItems);
  });

  return sortedMap;
};

// 특정 프레임과 트랙에서 모든 가능한 트랜지션 경계 찾기
export const findAllTransitionEdges = (
  tracks: TrackType[],
  items: Record<string, EditorStarterItem>,
): TransitionEdge[] => {
  const edges: TransitionEdge[] = [];

  tracks.forEach((track, trackIndex) => {
    const trackItems = track.items
      .map((itemId) => items[itemId])
      .filter((item): item is EditorStarterItem => !!item)
      .sort((a, b) => a.from - b.from);

    for (let i = 0; i < trackItems.length - 1; i++) {
      const sourceItem = trackItems[i];
      const targetItem = trackItems[i + 1];

      if (!sourceItem || !targetItem) continue;

      // Transition 적용 가능한 아이템 타입인지 확인 (image, video, gif만 허용)
      if (
        !isTransitionableItem(sourceItem) ||
        !isTransitionableItem(targetItem)
      ) {
        continue;
      }

      const sourceEnd = sourceItem.from + sourceItem.durationInFrames;
      const targetStart = targetItem.from;

      // 정확히 인접해 있는 경우만 허용 (빈 프레임이 없어야 함)
      if (sourceEnd === targetStart) {
        edges.push({
          sourceItemId: sourceItem.id,
          targetItemId: targetItem.id,
          edgeFrame: sourceEnd,
          trackId: track.id,
          trackIndex,
        });
      }
    }
  });

  return edges;
};

// 트랙 인덱스를 Y 좌표에서 계산
export interface TrackLayout {
  top: number;
  height: number;
}

export const getTrackIndexFromY = (
  clientY: number,
  containerRect: DOMRect,
  tracksLayout: TrackLayout[],
  scrollTop: number,
  headerHeight: number,
): number | null => {
  const relativeY = clientY - containerRect.top + scrollTop - headerHeight;

  for (let i = 0; i < tracksLayout.length; i++) {
    const track = tracksLayout[i];
    if (!track) continue;
    if (relativeY >= track.top && relativeY < track.top + track.height) {
      return i;
    }
  }

  return null;
};
